/**
 * NarrativeContextDisplay - Collapsible display for narrative context in chat messages
 *
 * Shows the AI-generated narrative context including:
 * - Theme and tone
 * - Voice style (pace, personality, tone)
 * - TTS prompt (single prompt for all audio generation)
 *
 * Design: Purple/violet theme to distinguish from emerald script updates
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Theater, Mic2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// Narrative context structure from video-scripts.json
export interface NarrativeContext {
  openingHook: string;
  keyTheme: string;
  tone: string;
  resolution: string;
  baseVoiceStyle?: {
    pace: string;
    personality: string;
    tone: string;
  };
  // Single TTS prompt (preferred)
  audioPrompt?: string;
  // Legacy fields for backward compatibility
  videoPromptFlash?: string;
  videoPromptPro?: string;
}

interface NarrativeContextDisplayProps {
  context: NarrativeContext;
  /** @deprecated Model selection no longer used - single TTS model configured via env var */
  selectedModel?: 'flash' | 'pro';
  className?: string;
}

export const NarrativeContextDisplay: React.FC<NarrativeContextDisplayProps> = ({
  context,
  selectedModel: _selectedModel, // Deprecated
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPromptDetails, setShowPromptDetails] = useState(false);

  // Get the TTS prompt - prefer audioPrompt, fall back to legacy fields
  const activePrompt = context.audioPrompt || context.videoPromptPro || context.videoPromptFlash;

  return (
    <div className={cn(
      "mt-3 border border-primary/30 rounded-lg overflow-hidden",
      className
    )}>
      {/* Collapsed header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-accent hover:bg-accent dark:hover:bg-accent/50 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-primary dark:text-accent-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-primary dark:text-accent-foreground" />
        )}
        <Theater className="h-4 w-4 text-primary dark:text-accent-foreground" />
        <span className="text-sm font-medium text-accent-foreground">
          Narrative Context
        </span>
        {/* Quick preview badges when collapsed */}
        {!isExpanded && context.tone && (
          <span className="ml-auto text-xs text-primary dark:text-accent-foreground truncate max-w-[200px]">
            {context.tone}
          </span>
        )}
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-3 py-3 space-y-3 bg-white dark:bg-gray-900">
          {/* Theme */}
          {context.keyTheme && (
            <div className="space-y-1">
              <div className="text-xs font-medium text-primary dark:text-accent-foreground uppercase tracking-wider">
                Theme
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {context.keyTheme}
              </p>
            </div>
          )}

          {/* Tone */}
          {context.tone && (
            <div className="space-y-1">
              <div className="text-xs font-medium text-primary dark:text-accent-foreground uppercase tracking-wider">
                Tone
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {context.tone}
              </p>
            </div>
          )}

          {/* Voice Style Badges */}
          {context.baseVoiceStyle && (
            <div className="space-y-1.5">
              <div className="text-xs font-medium text-primary dark:text-accent-foreground uppercase tracking-wider">
                Voice Style
              </div>
              <div className="flex flex-wrap gap-1.5">
                {context.baseVoiceStyle.pace && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent text-accent-foreground">
                    {context.baseVoiceStyle.pace}
                  </span>
                )}
                {context.baseVoiceStyle.personality && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent text-accent-foreground">
                    {context.baseVoiceStyle.personality}
                  </span>
                )}
                {context.baseVoiceStyle.tone && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent text-accent-foreground">
                    {context.baseVoiceStyle.tone}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Opening Hook */}
          {context.openingHook && (
            <div className="space-y-1">
              <div className="text-xs font-medium text-primary dark:text-accent-foreground uppercase tracking-wider">
                Opening Hook
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                "{context.openingHook}"
              </p>
            </div>
          )}

          {/* Resolution */}
          {context.resolution && (
            <div className="space-y-1">
              <div className="text-xs font-medium text-primary dark:text-accent-foreground uppercase tracking-wider">
                Resolution
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {context.resolution}
              </p>
            </div>
          )}

          {/* TTS Prompt - Collapsible */}
          {activePrompt && (
            <div className="pt-2 border-t border-primary/30">
              <button
                onClick={() => setShowPromptDetails(!showPromptDetails)}
                className="flex items-center gap-1.5 text-xs font-medium text-primary dark:text-accent-foreground hover:text-accent-foreground dark:hover:text-accent-foreground transition-colors"
              >
                <Mic2 className="h-3.5 w-3.5" />
                <span>TTS Prompt</span>
                {showPromptDetails ? (
                  <ChevronDown className="h-3 w-3 ml-1" />
                ) : (
                  <ChevronRight className="h-3 w-3 ml-1" />
                )}
              </button>
              {showPromptDetails && (
                <div className="mt-2 p-2 bg-accent rounded text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono">
                  {activePrompt}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NarrativeContextDisplay;
